/**
 * **차트 색의 정본** — 계열(범주) 색 여덟 칸과 채널 고정색.
 *
 * 🔑 범주 색은 **정해진 차례로만** 쓴다 — 여덟 칸을 넘는 계열은 돌려쓰지 않고 `CHART_OTHER` 회색으로 접는다.
 * 🔑 칸의 차례가 색약 안전 장치다 — 이웃한 두 칸이 색약 시뮬레이션에서도 갈리도록 고른 순서다.
 *  바꾸면 dataviz 스킬의 팔레트 검증 스크립트로 라이트(#FFFFFF)·다크(#1C252E) 둘 다 다시 돌린다.
 * ⛔ 상태색(`success`·`warning`·`error`)을 범주에 쓰지 마라 — 「좋다·나쁘다」로 읽힌다.
 * ⚠️ 1번 칸은 브랜드 `primary.main` 이다(라이트). 다크는 같은 색상의 한 단계 어두운 값이다.
 */

import platforms from 'src/constants/platform-catalog';

// ----------------------------------------------------------------------

export type ChartMode = 'light' | 'dark';

/** 범주 색 여덟 칸 — 파랑 · 주황 · 청록 · 노랑 · 분홍 · 초록 · 보라 · 빨강 */
export const CHART_SERIES: Record<ChartMode, readonly string[]> = {
  light: ['#10AFEA', '#EB6834', '#1BAF7A', '#EDA100', '#E87BA4', '#008300', '#4A3AA7', '#E34948'],
  dark: ['#0E9FD6', '#D95926', '#199E70', '#C98500', '#D55181', '#008300', '#9085E9', '#E66767'],
};

/** 여덟 칸 밖 · 「그 밖」 조각 — 색이 없는 자리 */
export const CHART_OTHER: Record<ChartMode, string> = { light: '#A7A7A7', dark: '#637381' };

/**
 * 채널마다 고정한 칸 — **모든 화면에서 같은 채널은 같은 색**이다.
 * ⚠️ 카탈로그(`constants/platform-catalog`)의 색은 로고색이라 X·Threads·TikTok 이 모두 검정이다 —
 *   선·조각을 가르지 못해 차트는 이 표를 쓴다. 표에 없는 채널은 `CHART_OTHER` 다.
 */
const CHANNEL_SLOT: Record<string, number> = {
  x: 0,
  reddit: 1,
  tiktok: 2,
  navercafe: 3,
  instagram: 4,
  naverblog: 5,
  threads: 6,
  youtube: 7,
};

/** 카탈로그의 채널 id·화면 이름(소문자) → id — 이름표(`N.Blog`)로 넘어와도 같은 채널로 읽는다 */
const CHANNEL_ID = new Map<string, string>(
  platforms.flatMap((p) => [
    [String(p.id).toLowerCase(), String(p.id)],
    [String(p.label).toLowerCase(), String(p.id)],
  ]),
);

/** `i` 번째 계열의 색 — 여덟 칸을 넘으면 회색(돌려쓰지 않는다) */
export function chartSeriesColor(index: number, mode: ChartMode = 'light'): string {
  return CHART_SERIES[mode][index] ?? CHART_OTHER[mode];
}

/** 카탈로그에 있는 채널인가 — id 든 화면 이름이든 */
export function isChartChannel(channel: string | null | undefined): boolean {
  return CHANNEL_ID.has(String(channel ?? '').trim().toLowerCase());
}

/**
 * 채널 색.
 * @param channel 색인의 `source` 값(`instagram` · `naverblog` …) 또는 화면 이름(`N.Blog`), 대소문자 무관
 * @returns 고정 칸의 색 · 표에 없으면 `CHART_OTHER`
 */
export function chartChannelColor(channel: string | null | undefined, mode: ChartMode = 'light'): string {
  const id = CHANNEL_ID.get(String(channel ?? '').trim().toLowerCase());
  const slot = id === undefined ? undefined : CHANNEL_SLOT[id];
  return slot === undefined ? CHART_OTHER[mode] : CHART_SERIES[mode][slot];
}
